/**
 * Conteúdo da aba DICI no relatório.
 * Exibe e gera relatórios DICI ANATEL (SCM) - listagem de DICIs gerados, gerar novo, visualizar, baixar CSV e excluir.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  Router as RouterIcon,
  Speed as SpeedIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  HelpOutline as HelpIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import diciService, { DiciRegistro, DiciGeradoItem } from '../../services/diciService';
import { escritorioService } from '../../services/escritorioService';
import { Escritorio } from '../../types';
import { format } from 'date-fns';
import SysDateRangePicker from '../../components/SysDateRangePicker';

const MESES_NOMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const RelatorioDiciTab: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const [helpOpen, setHelpOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedDici, setSelectedDici] = useState<DiciGeradoItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [diciToDelete, setDiciToDelete] = useState<DiciGeradoItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [gerarModalOpen, setGerarModalOpen] = useState(false);
  const [gerarMes, setGerarMes] = useState<string>(format(new Date(), 'yyyy-MM'));
  const [gerarEscritorioId, setGerarEscritorioId] = useState<number | ''>('');
  const [gerarSomenteDici, setGerarSomenteDici] = useState<boolean>(true);
  const [gerarAtrelarNfcom, setGerarAtrelarNfcom] = useState<boolean>(false);
  const [gerarPreviewData, setGerarPreviewData] = useState<DiciRegistro[]>([]);
  const [gerarPreviewLoading, setGerarPreviewLoading] = useState<boolean>(false);
  const [gerarSaving, setGerarSaving] = useState<boolean>(false);
  const [gerarError, setGerarError] = useState<string | null>(null);
  const [diciExistente, setDiciExistente] = useState<boolean>(false);
  const [diciExistenteMsg, setDiciExistenteMsg] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(new Date().getFullYear(), 11, 31),
  });
  const [escritorios, setEscritorios] = useState<Escritorio[]>([]);
  const [dicisGerados, setDicisGerados] = useState<DiciGeradoItem[]>([]);
  const [loadingGerados, setLoadingGerados] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsRow, setDetailsRow] = useState<DiciRegistro | null>(null);

  useEffect(() => {
    loadEscritorios();
  }, []);

  useEffect(() => {
    loadDicisGerados();
  }, [dateRange]);

  useEffect(() => {
    if (gerarModalOpen && gerarMes) {
      loadGerarPreview();
      checkDiciExistente();
    }
  }, [gerarMes, gerarEscritorioId, gerarSomenteDici, gerarAtrelarNfcom, gerarModalOpen]);

  const loadEscritorios = async () => {
    try {
      const response = await escritorioService.getForSelect({ ativo: 'true' });
      if (response.success) {
        setEscritorios(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar escritórios:', err);
    }
  };

  const loadDicisGerados = async () => {
    setLoadingGerados(true);
    try {
      const startDateStr = dateRange.startDate ? format(dateRange.startDate, 'yyyy-MM-dd') : undefined;
      const endDateStr = dateRange.endDate ? format(dateRange.endDate, 'yyyy-MM-dd') : undefined;
      const response = await diciService.listarGerados(undefined, startDateStr, endDateStr);
      if (response && response.success) {
        setDicisGerados(response.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar DICIs gerados:', err);
    } finally {
      setLoadingGerados(false);
    }
  };

  const checkDiciExistente = async () => {
    try {
      const officeId = gerarEscritorioId === '' ? undefined : Number(gerarEscritorioId);
      const response = await diciService.verificarExistente(gerarMes, officeId);
      if (response && response.success) {
        setDiciExistente(response.existe);
        if (response.existe && response.data) {
          const usuario = response.data.usuario?.nome || 'Usuário desconhecido';
          const dataGeracao = response.data.createdAt
            ? new Date(response.data.createdAt).toLocaleDateString('pt-BR', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })
            : 'data desconhecida';
          setDiciExistenteMsg(`Já existe um DICI gerado para este período por ${usuario} em ${dataGeracao}.`);
        } else {
          setDiciExistenteMsg('');
        }
      }
    } catch (err) {
      console.error('Erro ao verificar DICI existente:', err);
    }
  };

  const loadGerarPreview = async () => {
    setGerarPreviewLoading(true);
    setGerarError(null);
    try {
      const officeId = gerarEscritorioId === '' ? undefined : Number(gerarEscritorioId);
      const response = await diciService.getPreview(gerarMes, officeId, gerarSomenteDici, gerarAtrelarNfcom);
      if (response && response.success) {
        setGerarPreviewData(response.data || []);
      }
    } catch (err: any) {
      console.error('[DICI] Erro ao carregar prévia no modal:', err);
      setGerarError('Erro ao carregar prévia dos dados.');
    } finally {
      setGerarPreviewLoading(false);
    }
  };

  const handleGravarDici = async () => {
    setGerarSaving(true);
    setGerarError(null);
    try {
      const officeId = gerarEscritorioId === '' ? undefined : Number(gerarEscritorioId);
      const response = await diciService.gravarDici(gerarMes, officeId, gerarSomenteDici, gerarAtrelarNfcom);
      if (response && response.success) {
        setSnackbar({ open: true, message: response.message, severity: 'success' });
        setGerarModalOpen(false);
        loadDicisGerados();
      } else {
        setSnackbar({ open: true, message: response?.message || 'Erro ao gravar DICI.', severity: 'warning' });
      }
    } catch (err: any) {
      console.error('Erro ao gravar DICI:', err);
      const msg = err?.response?.data?.message || err?.message || 'Erro ao gravar DICI.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setGerarSaving(false);
    }
  };

  const handleOpenModal = () => {
    setGerarMes(format(new Date(), 'yyyy-MM'));
    setGerarEscritorioId('');
    setGerarSomenteDici(true);
    setGerarAtrelarNfcom(false);
    setGerarPreviewData([]);
    setGerarError(null);
    setDiciExistente(false);
    setDiciExistenteMsg('');
    setGerarModalOpen(true);
  };

  const handleViewDici = (dici: DiciGeradoItem) => {
    setSelectedDici(dici);
    setViewModalOpen(true);
  };

  const handleDownloadCsvFromGerado = async (dici: DiciGeradoItem) => {
    setDownloading(true);
    try {
      await diciService.gerarCsv(
        dici.mes_referencia,
        dici.escritorio_id || undefined,
        dici.somente_dici,
        dici.atrelar_nfcom
      );
    } catch (err) {
      console.error('Erro ao baixar CSV:', err);
      setSnackbar({ open: true, message: 'Erro ao gerar arquivo CSV.', severity: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteClick = (dici: DiciGeradoItem) => {
    setDiciToDelete(dici);
    setDeleteModalOpen(true);
  };

  const confirmDeleteDici = async () => {
    if (!diciToDelete) return;
    setDeleting(true);
    try {
      const response = await diciService.excluirDici(diciToDelete.id);
      if (response && response.success) {
        setSnackbar({ open: true, message: 'DICI excluído com sucesso!', severity: 'success' });
        loadDicisGerados();
      } else {
        setSnackbar({ open: true, message: 'Erro ao excluir DICI.', severity: 'error' });
      }
    } catch (err: any) {
      console.error('Erro ao excluir:', err);
      setSnackbar({ open: true, message: err?.response?.data?.message || 'Erro ao excluir DICI.', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
      setDiciToDelete(null);
    }
  };

  const handleOpenDetails = (row: DiciRegistro) => {
    setDetailsRow(row);
    setDetailsModalOpen(true);
  };

  const formatMesReferencia = (mesRef: string) => {
    const [ano, mes] = mesRef.split('-');
    const mesNum = parseInt(mes || '0', 10);
    return `${MESES_NOMES[mesNum - 1] || mes}/${ano}`;
  };

  const totalAcessosPreview = gerarPreviewData.reduce((acc, d) => acc + d.quantidade, 0);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 1 }}>
        O DICI é o sistema de coleta de dados da ANATEL. O relatório mensal (SCM) deve ser enviado até o dia 15 de cada mês, contendo os dados dos acessos ativos no último dia do mês anterior.
      </Alert>

      <Paper elevation={2} sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}>
        <Box p={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <CalendarIcon color="action" />
            DICIs Gerados
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box width={300}>
              <SysDateRangePicker value={dateRange} onChange={setDateRange} />
            </Box>
            <Chip label={`${dicisGerados.length} registros`} color="default" variant="outlined" />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              size="medium"
              sx={{ borderRadius: 2, fontWeight: 'bold' }}
            >
              Gerar DICI
            </Button>
            <Tooltip title="Como funciona este relatório?">
              <IconButton color="primary" onClick={() => setHelpOpen(true)}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Divider />
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                },
              }}>
                <TableCell>Competência</TableCell>
                <TableCell>Escritório</TableCell>
                <TableCell align="center">Registros</TableCell>
                <TableCell align="center">Acessos</TableCell>
                <TableCell>Filtros</TableCell>
                <TableCell>Gerado por</TableCell>
                <TableCell>Data de Geração</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingGerados ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : dicisGerados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary" variant="subtitle1">
                      Nenhum DICI gerado encontrado para o período.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                dicisGerados.map((dici) => (
                  <TableRow key={dici.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Chip
                        label={formatMesReferencia(dici.mes_referencia)}
                        color="primary"
                        variant="filled"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {dici.escritorio
                        ? (dici.escritorio.nome_fantasia || dici.escritorio.razao_social)
                        : 'Todos'}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Chip label={dici.total_registros} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Chip label={dici.total_acessos} size="small" color="success" variant="filled" />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {dici.somente_dici && (
                          <Chip label="Gera DICI" size="small" color="info" variant="outlined" />
                        )}
                        {dici.atrelar_nfcom && (
                          <Chip label="NFCom" size="small" color="secondary" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{dici.usuario?.nome || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {dici.createdAt
                        ? new Date(dici.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })
                        : '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Box display="flex" justifyContent="center">
                        <Tooltip title="Visualizar Detalhes">
                          <IconButton color="primary" onClick={() => handleViewDici(dici)} size="small">
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Baixar CSV">
                          <IconButton
                            color="success"
                            onClick={() => handleDownloadCsvFromGerado(dici)}
                            disabled={downloading}
                            size="small"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir DICI">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(dici)}
                            disabled={deleting}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal de Visualizar DICI */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: '60vh' } }}>
        {selectedDici && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <AssessmentIcon color="primary" />
                <Typography variant="h6">Detalhes do DICI - {formatMesReferencia(selectedDici.mes_referencia)}</Typography>
              </Box>
              <IconButton onClick={() => setViewModalOpen(false)}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Escritório</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedDici.escritorio ? (selectedDici.escritorio.nome_fantasia || selectedDici.escritorio.razao_social) : 'Todos'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Gerado por / Data</Typography>
                    <Typography variant="body2">
                      <b>{selectedDici.usuario?.nome}</b> em {new Date(selectedDici.createdAt).toLocaleDateString('pt-BR')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Totais</Typography>
                    <Box display="flex" gap={1}>
                      <Chip label={`${selectedDici.total_registros} linhas`} size="small" />
                      <Chip label={`${selectedDici.total_acessos} acessos`} size="small" color="primary" />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">Filtros Aplicados</Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {selectedDici.somente_dici && <Chip label="Gera DICI" size="small" color="info" variant="outlined" />}
                      {selectedDici.atrelar_nfcom && <Chip label="NFCom" size="small" color="secondary" variant="outlined" />}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
              <Divider sx={{ mb: 2 }}><Chip label="Dados Consolidados" size="small" /></Divider>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                        fontWeight: 700,
                        fontSize: '0.73rem',
                        py: 0.8,
                        borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                      },
                    }}>
                      <TableCell>FISTEL</TableCell>
                      <TableCell>Escritório</TableCell>
                      <TableCell>UF/Município</TableCell>
                      <TableCell>Tipo Cliente</TableCell>
                      <TableCell>Tecnologia</TableCell>
                      <TableCell>Velocidade</TableCell>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Qtd. Acessos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      let dados = selectedDici.dados_consolidados;
                      if (typeof dados === 'string') {
                        try { dados = JSON.parse(dados); } catch (e) { dados = []; }
                      }
                      if (!Array.isArray(dados)) dados = [];
                      if (dados.length > 0) {
                        return dados.map((row: any, index: number) => (
                          <TableRow key={index} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{row.fistel}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{row.escritorio_nome}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{row.municipio} - {row.uf}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{row.tipo_cliente}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{row.tipo_meio}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{row.faixa_velocidade}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{row.tipo_produto}</TableCell>
                            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                              <Tooltip title="Clique para ver detalhes dos clientes">
                                <Chip
                                  label={row.quantidade}
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  onClick={() => handleOpenDetails(row)}
                                  sx={{ cursor: 'pointer', '&:hover': { filter: 'brightness(1.1)' } }}
                                />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ));
                      }
                      return (
                        <TableRow>
                          <TableCell colSpan={8} align="center">Nenhum dado consolidado disponível.</TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setViewModalOpen(false)} color="primary">Fechar</Button>
              <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={() => handleDownloadCsvFromGerado(selectedDici)}>
                Baixar CSV
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Modal de Gerar DICI */}
      <Dialog open={gerarModalOpen} onClose={() => !gerarSaving && setGerarModalOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: '60vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e0e0e0' }}>
          <AddIcon color="primary" />
          Gerar Novo DICI
        </DialogTitle>
        <DialogContent sx={{ p: 1.5 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={5.5} md={3}>
              <TextField
                label="Mês de Competência"
                type="month"
                fullWidth
                value={gerarMes}
                onChange={(e) => setGerarMes(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={6.5} md={3}>
              <TextField
                select
                label="Escritório / Filial"
                fullWidth
                value={gerarEscritorioId}
                onChange={(e) => setGerarEscritorioId(e.target.value as number | '')}
                size="small"
              >
                <MenuItem value="">Todos os Escritórios</MenuItem>
                {escritorios.map((esc) => (
                  <MenuItem key={esc.id} value={esc.id}>
                    {esc.nome_fantasia || esc.razao_social} ({esc.fistel_anatel || 'Sem FISTEL'})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gerarAtrelarNfcom}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setGerarAtrelarNfcom(checked);
                      if (checked) setGerarSomenteDici(false);
                    }}
                    color="secondary"
                  />
                }
                label={<Typography variant="body2">Atrelar a <b>NFCom geradas</b></Typography>}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gerarSomenteDici}
                    onChange={(e) => setGerarSomenteDici(e.target.checked)}
                    color="primary"
                    disabled={gerarAtrelarNfcom}
                  />
                }
                label={<Typography variant="body2" color={gerarAtrelarNfcom ? 'text.disabled' : 'text.primary'}>Apenas com <b>Gera DICI</b></Typography>}
              />
            </Grid>
          </Grid>
          {diciExistente && (
            <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>{diciExistenteMsg}</Alert>
          )}
          {gerarError && <Alert severity="error" sx={{ mt: 2 }}>{gerarError}</Alert>}
          <Box mt={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                <PublicIcon color="action" /> Prévia dos Dados Consolidados
              </Typography>
              <Box display="flex" gap={1}>
                <Chip label={`${gerarPreviewData.length} registros`} color="default" variant="outlined" />
                <Chip label={`${totalAcessosPreview} acessos`} color="primary" variant="filled" />
              </Box>
            </Box>
            <Divider />
            <TableContainer sx={{ maxHeight: 350, mt: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                      fontWeight: 700,
                      fontSize: '0.73rem',
                      py: 0.8,
                      borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                    },
                  }}>
                    <TableCell>FISTEL</TableCell>
                    <TableCell>Escritório</TableCell>
                    <TableCell>UF/Município</TableCell>
                    <TableCell>Tipo Cliente</TableCell>
                    <TableCell>Tecnologia</TableCell>
                    <TableCell>Velocidade</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Qtd. Acessos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gerarPreviewLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 1 }}><CircularProgress /></TableCell>
                    </TableRow>
                  ) : gerarPreviewData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary" variant="subtitle1">Nenhum registro encontrado para o período selecionado.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    gerarPreviewData.map((row, index) => (
                      <TableRow key={index} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.fistel}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.escritorio_nome}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.municipio} - {row.uf}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.tipo_cliente}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.tipo_meio}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.faixa_velocidade}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{row.tipo_produto}</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                          <Tooltip title="Clique para ver detalhes dos clientes">
                            <Chip
                              label={row.quantidade}
                              size="small"
                              color="primary"
                              variant="filled"
                              onClick={() => handleOpenDetails(row)}
                              sx={{ cursor: 'pointer', '&:hover': { filter: 'brightness(1.1)' } }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 1, py: 1 }}>
          <Button onClick={() => setGerarModalOpen(false)} disabled={gerarSaving} color="inherit">Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleGravarDici}
            disabled={gerarSaving || gerarPreviewLoading || gerarPreviewData.length === 0 || diciExistente}
            startIcon={gerarSaving ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
            sx={{ fontWeight: 'bold', px: 4 }}
          >
            {gerarSaving ? 'Gravando...' : 'Gerar DICI'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Ajuda */}
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon color="primary" /> Entendendo o Relatório DICI (SCM)
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            O <b>DICI (Dados de Indicadores e Coletas de Informação)</b> é o sistema oficial da ANATEL para coleta de dados das prestadoras de serviços de telecomunicações.
          </Typography>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>1. Responsabilidade e Prazos</Typography>
          <Typography variant="body2" paragraph>
            Todas as empresas com outorga SCM (Internet) devem enviar os dados de acessos ativos mensalmente através do sistema DICI.
            O prazo de envio é até o <b>dia 15 de cada mês</b>, referente aos dados do último dia do mês anterior.
          </Typography>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>2. Como os dados são gerados?</Typography>
          <Typography variant="body2" paragraph>Este sistema consolida automaticamente seus contratos ativos com base nas seguintes regras:</Typography>
          <List>
            <ListItem><ListItemIcon><PublicIcon fontSize="small" /></ListItemIcon><ListItemText primary="Geografia: Agrupado por UF e Município do endereço de instalação." /></ListItem>
            <ListItem><ListItemIcon><SpeedIcon fontSize="small" /></ListItemIcon><ListItemText primary="Velocidade: Utiliza a velocidade real de download vinculada ao plano do serviço." /></ListItem>
            <ListItem><ListItemIcon><RouterIcon fontSize="small" /></ListItemIcon><ListItemText primary="Tecnologia: Identifica se o meio de acesso é Fibra Óptica, Rádio, etc." /></ListItem>
            <ListItem><ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon><ListItemText primary="Perfil: Separação entre Pessoa Física (Residencial) e Pessoa Jurídica (Corporativo)." /></ListItem>
          </List>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>3. Importante</Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Para que o relatório seja preciso, certifique-se de que:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Os serviços nos contratos estão com o <b>Meio de Acesso</b> e <b>Tipo de Atendimento</b> preenchidos.</li>
              <li>A opção <b>Gera DICI</b> está marcada nos serviços que devem ser reportados.</li>
              <li>O endereço de instalação do contrato possui <b>CEP</b> válido e o <b>Município</b> correto.</li>
              <li>O <b>FISTEL</b> da empresa está preenchido no cadastro do Escritório/Filial.</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)} color="primary" variant="contained">Entendi</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Exclusão */}
      <Dialog open={deleteModalOpen} onClose={() => !deleting && setDeleteModalOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o DICI de <b>{diciToDelete ? formatMesReferencia(diciToDelete.mes_referencia) : ''}</b>?
          </Typography>
          <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>Esta ação não pode ser desfeita.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={deleting} color="inherit">Cancelar</Button>
          <Button onClick={confirmDeleteDici} disabled={deleting} color="error" variant="contained" autoFocus>
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal de Detalhes dos Acessos */}
      <Dialog open={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon color="primary" />
            <Typography variant="h6">Detalhes dos Clientes / Acessos</Typography>
          </Box>
          <IconButton onClick={() => setDetailsModalOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {detailsRow && (
            <Box p={2}>
              <Box mb={2} p={1} bgcolor={alpha(primario, 0.05)} borderRadius={1} border={`1px solid ${alpha(primario, 0.1)}`}>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Município/UF:</Typography> <Typography variant="body2" fontWeight="bold">{detailsRow.municipio} - {detailsRow.uf}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Perfil:</Typography> <Typography variant="body2" fontWeight="bold">{detailsRow.tipo_cliente}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Tecnologia:</Typography> <Typography variant="body2" fontWeight="bold">{detailsRow.tipo_meio}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Velocidade:</Typography> <Typography variant="body2" fontWeight="bold">{detailsRow.faixa_velocidade}</Typography></Grid>
                </Grid>
              </Box>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) => t.palette.background.default,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                      },
                    }}>
                      <TableCell>Nome do Cliente</TableCell>
                      <TableCell align="center">ID Serviço</TableCell>
                      <TableCell align="center">ID Contrato</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailsRow.servicos_detalhes && (detailsRow as any).servicos_detalhes.length > 0 ? (
                      (detailsRow as any).servicos_detalhes.map((s: any, idx: number) => (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ py: 0.8, fontSize: '0.8rem' }}>{s.cliente_nome}</TableCell>
                          <TableCell sx={{ py: 0.8, fontSize: '0.8rem' }} align="center">{s.servico_id}</TableCell>
                          <TableCell onClick={() => navigate(`/contracts/${s.contrato_id}`)} sx={{ cursor: 'pointer', py: 0.8, fontSize: '0.8rem' }} align="center">{s.contrato_id}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 2 }}>Nenhum detalhe disponível para este registro.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsModalOpen(false)} color="primary">Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RelatorioDiciTab;
